import React, { useState } from "react";
import { Typography, Box, useMediaQuery, Grid, Tabs, Tab, Container, Paper, Button, List, ListItem, ListItemText, IconButton, CircularProgress, Card, CardContent, Stack, Chip, Avatar, Fade, Zoom } from '@mui/material';
import { useTheme, alpha } from '@mui/material/styles';
import MiniCreatorSearch from "../../../../Components/miniCreatorSearch";
import { ToggleSet } from "../../../../Utils/constants";
import { 
  Delete as DeleteIcon,
  PersonAdd as PersonAddIcon,
  Search as SearchIcon,
  People as PeopleIcon,
  Send as SendIcon
} from '@mui/icons-material';
import { useMutation } from "react-query";
import API from "../../../../API";

const CreatorList = ({ castingCall, invitedCreators, selectedItems, applicatedSet, handlePickCreator }) => {
  const theme = useTheme();
  
  return (
    <Box sx={{ height: { xs: '75vh', md: '70vh' }, minHeight: '0' }}>
      <Paper 
        sx={{ 
          width: '100%',
          height: '100%',
          borderRadius: 3,
          border: '1px solid',
          borderColor: 'divider',
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column'
        }}
        elevation={0}
      >
        {/* Header */}
        <Box 
          sx={{ 
            p: 3,
            bgcolor: alpha(theme.palette.primary.main, 0.05),
            borderBottom: '1px solid',
            borderColor: 'divider'
          }}
        >
          <Stack direction="row" alignItems="center" spacing={2}>
            <Box 
              sx={{ 
                p: 1,
                borderRadius: 2,
                bgcolor: 'primary.main',
                color: 'white',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
            >
              <SearchIcon />
            </Box>
            <Box>
              <Typography variant="h6" fontWeight={700}>
                Search Creators
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Find and select creators to invite to your casting call
              </Typography>
            </Box>
          </Stack>
        </Box>

        {/* Search Component */}
        <Box sx={{ flex: 1, overflow: 'hidden' }}>
          <MiniCreatorSearch
            searchSettings={{ promotion_types: castingCall?.promotion_types }}
            selectedCreators={new Set([...invitedCreators, ...selectedItems])}
            disabledCreators={applicatedSet}
            onCreatorPick={handlePickCreator}
          />
        </Box>
      </Paper>
    </Box>
  );
};

const InvitedList = ({ selectedItems, invitedCreators, handlePickCreator, postInvite, loadingInvite }) => {
  const theme = useTheme();
  
  return (
    <Paper 
      sx={{ 
        height: { xs: '75vh', md: '70vh' }, 
        display: 'flex', 
        flexDirection: 'column',
        borderRadius: 3,
        border: '1px solid',
        borderColor: 'divider'
      }} 
      elevation={0}
    >
      {/* New Invites Section */}
      <Box 
        sx={{ 
          p: 3,
          bgcolor: alpha(theme.palette.secondary.main, 0.05),
          borderBottom: '1px solid',
          borderColor: 'divider'
        }}
      >
        <Stack direction="row" alignItems="center" justifyContent="space-between">
          <Stack direction="row" alignItems="center" spacing={2}>
            <Box 
              sx={{ 
                p: 1,
                borderRadius: 2,
                bgcolor: 'secondary.main',
                color: 'white',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
            >
              <PersonAddIcon />
            </Box>
            <Box>
              <Typography variant="h6" fontWeight={700}>
                New Invites
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {selectedItems.size} creator{selectedItems.size !== 1 ? 's' : ''} selected
              </Typography>
            </Box>
          </Stack>
          
          <Button
            variant="contained"
            color="secondary"
            disabled={selectedItems.size < 1 || loadingInvite}
            onClick={postInvite}
            startIcon={loadingInvite ? <CircularProgress size={16} color="inherit" /> : <SendIcon />}
            sx={{ 
              borderRadius: 2,
              fontWeight: 600,
              px: 3
            }}
          >
            {loadingInvite ? "Sending..." : "Send Invites"}
          </Button>
        </Stack>
      </Box>

      {/* Selected Creators List */}
      <Box sx={{ flex: 1, overflow: 'auto', p: 2 }}>
        {selectedItems.size > 0 ? (
          <Stack spacing={1}>
            {[...selectedItems].map((creator, index) => (
              <Zoom key={creator} in timeout={200 + index * 50}>
                <Card 
                  sx={{ 
                    borderRadius: 2,
                    border: '1px solid',
                    borderColor: 'divider',
                    bgcolor: alpha(theme.palette.secondary.main, 0.05),
                    transition: 'all 0.2s ease'
                  }}
                >
                  <CardContent sx={{ py: 1.5, px: 2, '&:last-child': { pb: 1.5 } }}>
                    <Stack direction="row" alignItems="center" justifyContent="space-between">
                      <Stack direction="row" alignItems="center" spacing={2}>
                        <Avatar 
                          sx={{ 
                            width: 32, 
                            height: 32,
                            bgcolor: 'secondary.main',
                            fontSize: '0.875rem',
                            fontWeight: 600
                          }}
                        >
                          {creator[0]?.toUpperCase()}
                        </Avatar>
                        <Typography variant="body1" fontWeight={600}>
                          {creator}
                        </Typography>
                      </Stack>
                      <IconButton 
                        size="small"
                        color="error" 
                        onClick={() => handlePickCreator({ creator })}
                        sx={{ 
                          '&:hover': { 
                            bgcolor: alpha(theme.palette.error.main, 0.1) 
                          }
                        }}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Stack>
                  </CardContent>
                </Card>
              </Zoom>
            ))}
          </Stack>
        ) : (
          <Box 
            sx={{ 
              textAlign: 'center', 
              py: 6,
              color: 'text.secondary'
            }}
          >
            <PersonAddIcon sx={{ fontSize: 48, mb: 2, opacity: 0.5 }} />
            <Typography variant="body1" fontWeight={500}>
              No creators selected
            </Typography>
            <Typography variant="body2">
              Search and select creators to send invitations
            </Typography>
          </Box>
        )}
      </Box>

      {/* Divider */}
      <Box sx={{ px: 2 }}>
        <Box sx={{ height: 1, bgcolor: 'divider', my: 2 }} />
      </Box>

      {/* Existing Invited Creators */}
      <Box sx={{ p: 3 }}>
        <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 2 }}>
          <Box 
            sx={{ 
              p: 1,
              borderRadius: 2,
              bgcolor: 'success.main',
              color: 'white',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            <PeopleIcon />
          </Box>
          <Box>
            <Typography variant="h6" fontWeight={700}>
              Invited Creators
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {invitedCreators.length} creator{invitedCreators.length !== 1 ? 's' : ''} already invited
            </Typography>
          </Box>
        </Stack>

        {invitedCreators.length > 0 ? (
          <Stack spacing={1} sx={{ maxHeight: 200, overflow: 'auto' }}>
            {invitedCreators.map((creator, index) => (
              <Fade key={creator} in timeout={300 + index * 100}>
                <Card 
                  sx={{ 
                    borderRadius: 2,
                    border: '1px solid',
                    borderColor: 'divider',
                    bgcolor: alpha(theme.palette.success.main, 0.05)
                  }}
                >
                  <CardContent sx={{ py: 1.5, px: 2, '&:last-child': { pb: 1.5 } }}>
                    <Stack direction="row" alignItems="center" spacing={2}>
                      <Avatar 
                        sx={{ 
                          width: 32, 
                          height: 32,
                          bgcolor: 'success.main',
                          fontSize: '0.875rem',
                          fontWeight: 600
                        }}
                      >
                        {creator[0]?.toUpperCase()}
                      </Avatar>
                      <Typography variant="body1" fontWeight={600}>
                        {creator}
                      </Typography>
                      <Chip 
                        label="Invited" 
                        size="small" 
                        color="success"
                        sx={{ ml: 'auto', fontWeight: 500 }}
                      />
                    </Stack>
                  </CardContent>
                </Card>
              </Fade>
            ))}
          </Stack>
        ) : (
          <Box 
            sx={{ 
              textAlign: 'center', 
              py: 4,
              color: 'text.secondary'
            }}
          >
            <PeopleIcon sx={{ fontSize: 40, mb: 1, opacity: 0.5 }} />
            <Typography variant="body2">
              No creators have been invited yet
            </Typography>
          </Box>
        )}
      </Box>
    </Paper>
  );
};

const InvitedCreators = ({ invitedCreators, castingCall, refetchData }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [mobileTab, setMobileTab] = useState(0);
  const [selectedItems, setSelectedItems] = useState(new Set());
  const invitedSet = new Set(invitedCreators);
  const applicatedSet = new Set(castingCall.applications);

  const { mutate: postInvite, isLoading: loadingInvite } = useMutation({
    mutationFn: (input) => API.castingCalls.invite(castingCall.id, input),
    onSuccess: () => {
      refetchData();
      setSelectedItems(new Set());
    },
    onError: (error) => {
      console.error("Error uploading casting call:", error);
      alert(`An error occurred while creating your Casting Call: ${error?.response?.data?.error || error.message}`);
    },
  });

  const HandleInvite = () => {
    postInvite({ creators: [...selectedItems] });
  };

  const handlePickCreator = (creator) => {
    if (invitedSet.has(creator.creator) || applicatedSet.has(creator.creator))
      return;

    ToggleSet(creator.creator, selectedItems, setSelectedItems);
  };

  return (
    <Container maxWidth="xl" sx={{ py: 3 }}>
      {/* Mobile View */}
      {isMobile && (
        <Box sx={{ width: '100%' }}>
          <Paper 
            square 
            elevation={0}
            sx={{ 
              mb: 2,
              borderRadius: '8px 8px 0 0',
              border: '1px solid',
              borderColor: 'divider',
              borderBottom: 'none'
            }}
          >
            <Tabs
              value={mobileTab}
              onChange={(e, newValue) => setMobileTab(newValue)}
              variant="fullWidth"
              sx={{
                '& .MuiTab-root': {
                  textTransform: 'none',
                  fontWeight: 600,
                  fontSize: '1rem',
                  py: 2
                }
              }}
            >
              <Tab 
                label={
                  <Stack direction="row" alignItems="center" spacing={1}>
                    <SearchIcon fontSize="small" />
                    <span>Search</span>
                  </Stack>
                } 
              />
              <Tab 
                label={
                  <Stack direction="row" alignItems="center" spacing={1}>
                    <PeopleIcon fontSize="small" />
                    <span>Manage</span>
                    {selectedItems.size > 0 && (
                      <Chip 
                        label={selectedItems.size} 
                        size="small" 
                        color="secondary"
                        sx={{ ml: 0.5 }}
                      />
                    )}
                  </Stack>
                } 
              />
            </Tabs>
          </Paper>

          <Box>
            {mobileTab === 0 && (
              <CreatorList
                castingCall={castingCall}
                invitedCreators={invitedCreators}
                selectedItems={selectedItems}
                applicatedSet={applicatedSet}
                handlePickCreator={handlePickCreator}
              />
            )}
            {mobileTab === 1 && (
              <InvitedList
                selectedItems={selectedItems}
                invitedCreators={invitedCreators}
                handlePickCreator={handlePickCreator}
                postInvite={HandleInvite}
                loadingInvite={loadingInvite}
              />
            )}
          </Box>
        </Box>
      )}

      {/* Desktop View */}
      {!isMobile && (
        <Grid container spacing={3}>
          <Grid item xs={5}>
            <CreatorList
              castingCall={castingCall}
              invitedCreators={invitedCreators}
              selectedItems={selectedItems}
              applicatedSet={applicatedSet}
              handlePickCreator={handlePickCreator}
            />
          </Grid>
          <Grid item xs={7}>
            <InvitedList
              selectedItems={selectedItems}
              invitedCreators={invitedCreators}
              handlePickCreator={handlePickCreator}
              postInvite={HandleInvite}
              loadingInvite={loadingInvite}
            />
          </Grid>
        </Grid>
      )}
    </Container>
  );
};

export default InvitedCreators;